import type { ScheduleOrder } from './filterEvents'
import type { SnEvent } from './types'

/** A game as the schedule list consumes it — only `starts_at` matters here. */
interface ClampGame {
  starts_at?: string | null
  [key: string]: unknown
}

export interface ClampEventsArgs {
  events: SnEvent[] | null | undefined
  games: ClampGame[] | null | undefined
  /** True while more game pages remain to load. Drives the clamp. */
  hasMoreGames?: boolean
  order?: ScheduleOrder
}

const toTime = (iso?: string | null): number | null => {
  if (!iso) {
    return null
  }
  const time = new Date(iso).getTime()
  return Number.isNaN(time) ? null : time
}

/**
 * Holds back events that sit beyond the last loaded game.
 *
 * Games paginate (infinite scroll); events arrive complete and unpaginated.
 * Merging the two naively puts a December event above August games that simply
 * have not loaded yet — the list looks wrong, then reshuffles as the user
 * scrolls.
 *
 * So while more game pages remain, only events at or before the last loaded
 * game's `starts_at` are admitted; the rest appear as further pages arrive.
 * Once `hasMoreGames` is false the boundary lifts and every remaining event is
 * included — which also covers the single-page case, where nothing is ever
 * withheld.
 *
 * With no games loaded at all there is no boundary: if more pages are coming,
 * nothing is admitted (a first page is in flight); if not, the list is
 * events-only and all of them are admitted.
 *
 * Extracted from `mergeScheduleItems` so the day-bucket path (`MyScheduleList`)
 * and the flat-list path apply one identical rule.
 */
export const clampEventsToLoadedGames = ({
  events,
  games,
  hasMoreGames = false,
  order = 'asc',
}: ClampEventsArgs): SnEvent[] => {
  const eventList = events ?? []

  if (!hasMoreGames) {
    return eventList
  }

  const gameTimes = (games ?? [])
    .map((game) => toTime(game?.starts_at))
    .filter((time): time is number => time !== null)

  if (!gameTimes.length) {
    // First page still in flight — no boundary yet, so admit nothing rather
    // than briefly showing events alone above games about to arrive.
    return []
  }

  const isDesc = order === 'desc'
  // Games arrive server-sorted, but derive the boundary rather than assuming
  // index 0/length-1 holds it.
  const boundary = isDesc ? Math.min(...gameTimes) : Math.max(...gameTimes)

  return eventList.filter((event) => {
    const time = toTime(event?.starts_at)
    if (time === null) {
      return false
    }
    return isDesc ? time >= boundary : time <= boundary
  })
}
